// Writes for the Agent Kingdom.
//
// Pattern: every button on an agent sheet writes a command document to
// `agent_commands` (Grok Bot reads them with the MCP get_agent_commands tool at
// the top of each run and closes them with complete_agent_command), logs it to
// `agent_events`, and optimistically updates the agent doc so the map responds
// instantly. The agent's next report_agent_status is the source of truth.

import { doc, collection, addDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { STATUS } from "../config/worlds";

const LABEL = { approve: "Approved", retry: "Cleared & retried", pause: "Paused", resume: "Woke" };

export function makeAgentsApi(db, user) {
  const by = {
    uid: user?.uid ?? null,
    name: user?.displayName || null,
    email: user?.email || null,
  };

  async function send(agentId, type, optimistic, note = "") {
    await addDoc(collection(db, "agent_commands"), {
      agentId,
      type, // "approve" | "retry" | "pause" | "resume"
      payload: note ? { note } : {},
      status: "pending", // Grok Bot flips to consumed → done | failed
      createdBy: by,
      createdAt: serverTimestamp(),
    });
    await addDoc(collection(db, "agent_events"), {
      agentId,
      type: "command",
      message: `${LABEL[type]} by ${by.name || by.email || "a CRM user"}${note ? ` — ${note}` : ""}`,
      toAgentId: null,
      item: null,
      by,
      at: serverTimestamp(),
    });
    await setDoc(doc(db, "agents", agentId), { ...optimistic, updatedAt: serverTimestamp() }, { merge: true });
  }

  return {
    /** Approve whatever the agent submitted and release it back to work. */
    approve: (agentId, note) => send(agentId, "approve", { status: STATUS.WORKING, progress: 0 }, note),
    /** Clear a blocked agent and tell it to retry its current task. */
    retry: (agentId, note) => send(agentId, "retry", { status: STATUS.WORKING, progress: 10 }, note),
    pause: (agentId, note) => send(agentId, "pause", { status: STATUS.IDLE }, note),
    resume: (agentId, note) => send(agentId, "resume", { status: STATUS.WORKING }, note),
  };
}
